// Import Libraries
import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';

// MUI
import { Radio, RadioGroup, FormControlLabel, Typography } from '@mui/material';

// Import Services
import ScriptService from '../../services/scriptService';

// Setup Modal to Root
Modal.setAppElement('#root');

function ProcessingScriptModal({ modalIsOpen, setModalIsOpen, scriptId, scriptName }) {
  const [error, setError] = useState(null);
  const [uhOhError, setUhOhError] = useState(false);

  const [step, setStep] = useState('question');
  const [shootTiming, setShootTiming] = useState('');

  function closeModal() {
    setError(null);
    setUhOhError(false);
    setStep('question');
    setShootTiming('');
    setModalIsOpen(false);
    window.location.href = `/app/newProject/1`;
  }

  function goToManual() {
    const query = scriptName ? `?scriptName=${encodeURIComponent(scriptName)}` : '';
    window.location.href = `/app/newManualProject/2${query}`;
  }

  // Proceed immediately; save the answer in the background so the
  // network round-trip doesn't delay the step transition
  function goToProcessingStep() {
    if (!shootTiming) return;

    setStep('processing');

    ScriptService.SaveScriptTimeline(scriptId, shootTiming).catch((e) => {
      console.error('SaveScriptTimeline failed:', e);
    });
  }

  useEffect(() => {
    if (modalIsOpen) {
      setError(null);
      setUhOhError(false);
      setStep('question');
      setShootTiming('');
    }
  }, [modalIsOpen]);

  useEffect(() => {
    const setupPage = async () => {
      if (!modalIsOpen || !scriptId || uhOhError || step !== 'processing') return;

      try {
        let setupScript = { page_count: 0, scenes: [] };
        let counter = 0;

        await new Promise((resolve) => setTimeout(resolve, 20000));
        setupScript = await ScriptService.getScriptById(scriptId);
        setupScript.scenes = await ScriptService.getAllScenes(scriptId);

        while (
          setupScript.scenes.length < 2 &&
          counter < 20 &&
          setupScript.page_count !== setupScript.last_processed_page
        ) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          setupScript = await ScriptService.getScriptById(scriptId);
          setupScript.scenes = await ScriptService.getAllScenes(scriptId);
          counter++;
        }

        if (setupScript.scenes.length < 1) {
          // We couldn't parse any scenes, so don't leave a visible project
          // card on the user's dashboard. The script row is soft-deleted
          // (deleted_at), not purged, so we can still pull the uploaded file
          // and scene data later to see why parsing failed.
          ScriptService.deleteScript(scriptId).catch((e) => {
            console.error('Failed to clean up unparsed script:', e);
          });
          setUhOhError(true);
          return;
        }

        window.location.href = `/app/shotList/${scriptId}`;
      } catch (e) {
        console.error(e);
        setUhOhError(true);
      }
    };

    setupPage();
  }, [scriptId, modalIsOpen, step, uhOhError]);

  const renderQuestionStep = () => (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', gap: 18 }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: '#5A4BFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            marginTop: 6,
          }}
        >
          <FontAwesomeIcon icon={faWandMagicSparkles} style={{ color: '#fff', fontSize: 20 }} />
        </div>

        <div>
          <Typography variant="h6">When are you planning to shoot?</Typography>
          <Typography variant="body1" className="purpleText">
            While we process your script, we&apos;d love to learn more about your project.
          </Typography>
        </div>
      </div>

      {/* Options */}
      <RadioGroup
        value={shootTiming}
        onChange={(e) => setShootTiming(e.target.value)}
        style={{ marginTop: 20, marginBottom: 30 }}
      >
        {[
          { value: 'lt_month', label: 'Less than a month' },
          { value: 'next_3_months', label: 'Next 3 months' },
          { value: '3_6_months', label: '3-6 months' },
          { value: 'exploring', label: 'Just exploring / testing' },
        ].map((opt) => (
          <FormControlLabel
            key={opt.value}
            value={opt.value}
            control={
              <Radio
                sx={{
                  color: '#5A4BFF',
                  '&.Mui-checked': {
                    color: '#5A4BFF',
                  },
                }}
              />
            }
            label={<span>{opt.label}</span>}
          />
        ))}
      </RadioGroup>

      {/* Continue */}
      <div>
        <button
          onClick={goToProcessingStep}
          disabled={!shootTiming}
          className="buttonClone"
          style={{
            width: '100%',
          }}
        >
          Continue
        </button>
      </div>
    </div>
  );

  const renderProcessingStep = () => (
    <div className="DeepPadded" style={{ textAlign: 'center' }}>
      <img src="/Spinner-2.9s-297px.gif" alt="Loading" style={{ width: '50%', margin: '0 auto' }} />
      <div className="large bold">Processing your script</div>
      <div className="subHeader Padded">
        We are breaking it down into scenes. This could take a few minutes.
      </div>
    </div>
  );

  const renderErrorStep = () => (
    <>
      <div className="close-button" role="button" onClick={closeModal}>
        <FontAwesomeIcon icon={faTimes} />
      </div>
      <div className="sizeUp">
        <div className="modalHeader">O-oh!</div>
        <div className="modalContent">
          Something went wrong. Please try again.
          <br />
          <br />
          <a onClick={goToManual} className="purpleText link">
            Manually Enter Script
          </a>
        </div>
      </div>
    </>
  );

  return (
    <Modal isOpen={modalIsOpen} contentLabel="Upload Script" className="modal">
      {!uhOhError
        ? step === 'question'
          ? renderQuestionStep()
          : renderProcessingStep()
        : renderErrorStep()}
      {error && <p className="errorText">{error}</p>}
    </Modal>
  );
}

export default ProcessingScriptModal;
